import React, { useState, useEffect } from 'react';
import { typesApi } from '../api';
import { toast } from 'sonner';
import {
  BookType,
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  Loader2,
  CheckCircle,
  XCircle,
  FolderOpen,
} from 'lucide-react';

const Types = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    category: '',
    is_active: true,
  });

  useEffect(() => {
    loadTypes();
  }, []);

  const loadTypes = async () => {
    try {
      setLoading(true);
      const response = await typesApi.list();
      setTypes(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar tipos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (type = null) => {
    if (type) {
      setEditingType(type);
      setFormData({
        code: type.code || '',
        name: type.name || '',
        description: type.description || '',
        category: type.category || '',
        is_active: type.is_active !== false,
      });
    } else {
      setEditingType(null);
      setFormData({
        code: '',
        name: '',
        description: '',
        category: '',
        is_active: true,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingType(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name) {
      toast.error('O nome do tipo é obrigatório');
      return;
    }

    setSaving(true);

    const cleanedData = {
      ...formData,
      code: formData.code || null,
      description: formData.description || null,
      category: formData.category || null,
    };

    try {
      if (editingType) {
        await typesApi.update(editingType.id, cleanedData);
        toast.success('Tipo atualizado com sucesso!');
      } else {
        await typesApi.create(cleanedData);
        toast.success('Tipo cadastrado com sucesso!');
      }

      handleCloseModal();
      loadTypes();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = Array.isArray(detail)
        ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ')
        : (detail || 'Erro ao salvar tipo');
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type) => {
    if (!window.confirm(`Desativar o tipo "${type.name}"?`)) return;

    try {
      await typesApi.delete(type.id);
      toast.success('Tipo desativado com sucesso!');
      loadTypes();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Erro ao desativar tipo');
    }
  };

  const handleReactivate = async (type) => {
    try {
      await typesApi.reactivate(type.id);
      toast.success('Tipo reativado com sucesso!');
      loadTypes();
    } catch (error) {
      toast.error('Erro ao reativar tipo');
    }
  };

  // Get unique categories for filtering
  const categories = [...new Set(types.filter(t => t.category).map(t => t.category))];

  const filteredTypes = types.filter(t =>
    (t.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.code?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const activeTypes = filteredTypes.filter(t => t.is_active);
  const inactiveTypes = filteredTypes.filter(t => !t.is_active);

  return (
    <div className="space-y-6" data-testid="types-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BookType className="text-green-400" />
            Cadastro de Tipos
          </h1>
          <p className="text-gray-400 mt-1">Gerencie os tipos utilizados no sistema</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="btn-primary"
        >
          <Plus size={18} />
          Novo Tipo
        </button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
          <input
            type="text"
            placeholder="Buscar por nome ou código..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input w-full pl-10"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <BookType className="text-blue-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Total de Tipos</p>
              <p className="text-2xl font-bold text-blue-400">{types.length}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <CheckCircle className="text-green-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Ativos</p>
              <p className="text-2xl font-bold text-green-400">
                {activeTypes.length}
              </p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <XCircle className="text-red-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Inativos</p>
              <p className="text-2xl font-bold text-red-400">
                {inactiveTypes.length}
              </p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <FolderOpen className="text-purple-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Categorias</p>
              <p className="text-2xl font-bold text-purple-400">
                {categories.length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-green-500" size={32} />
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Código</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Nome</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Descrição</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Categoria</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredTypes.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    Nenhum tipo encontrado
                  </td>
                </tr>
              ) : (
                filteredTypes.map(t => (
                  <tr key={t.id} className="hover:bg-gray-800/50">
                    <td className="px-4 py-3">
                      <span className="font-mono text-white">{t.code}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-white">{t.name}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-400 max-w-xs truncate">
                      {t.description || '-'}
                    </td>
                    <td className="px-4 py-3">
                      {t.category ? (
                        <span className="px-2 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400">
                          {t.category}
                        </span>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {t.is_active ? (
                        <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400 flex items-center gap-1 w-fit">
                          <CheckCircle size={12} />
                          Ativo
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400 flex items-center gap-1 w-fit">
                          <XCircle size={12} />
                          Inativo
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleOpenModal(t)}
                          className="p-2 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded-lg"
                          title="Editar"
                        >
                          <Edit size={18} />
                        </button>
                        {t.is_active ? (
                          <button
                            onClick={() => handleDelete(t)}
                            className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg"
                            title="Desativar"
                          >
                            <Trash2 size={18} />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleReactivate(t)}
                            className="p-2 text-gray-400 hover:text-green-400 hover:bg-gray-700 rounded-lg"
                            title="Reativar"
                          >
                            <CheckCircle size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Form Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h2 className="text-lg font-semibold text-white">
                {editingType ? 'Editar Tipo' : 'Novo Tipo'}
              </h2>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Código</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="TP-001 (auto)"
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Nome <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Procedimento"
                    className="input w-full"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <FolderOpen size={14} className="inline mr-1" />
                  Categoria
                </label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="Ex: documento, auditoria, treinamento"
                  className="input w-full"
                  list="category-suggestions"
                />
                <datalist id="category-suggestions">
                  {categories.map(cat => (
                    <option key={cat} value={cat} />
                  ))}
                </datalist>
                <p className="text-xs text-gray-500 mt-1">Agrupe tipos por categoria para facilitar a organização</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Descrição</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrição do tipo..."
                  className="input w-full"
                  rows={2}
                />
              </div>

              <div className="flex items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-green-500"
                  />
                  <span className="text-sm font-medium text-gray-300">Tipo Ativo</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button type="button" onClick={handleCloseModal} className="btn-secondary" disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
                  {saving ? <><Loader2 size={18} className="animate-spin" /> Salvando...</> : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Types;
